import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck, FileText, Download, ExternalLink, LogOut,
  ChevronRight, Loader2, Shield, Lock, Eye, Zap, Settings, CheckCircle,
  Accessibility, Sparkles, ArrowRight, Clock,
} from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { ProgressRing } from '../ui-custom/ProgressRing';
import { Badge } from '../ui-custom/Badge';
import { Card } from '../ui-custom/Card';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = {
  security: { label: "Security", icon: Shield, color: "#2563eb" },
  availability: { label: "Availability", icon: Zap, color: "#059669" },
  processing_integrity: { label: "Processing Integrity", icon: Settings, color: "#7c3aed" },
  confidentiality: { label: "Confidentiality", icon: Lock, color: "#d97706" },
  privacy: { label: "Privacy", icon: Eye, color: "#dc2626" },
};

function ClientPortal() {
  const navigate = useNavigate();
  const { clientUser, clientLogout } = useClientAuth();
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [reports, setReports] = useState([]);
  const [wcagDocs, setWcagDocs] = useState([]);
  const [scanReportGroups, setScanReportGroups] = useState([]);
  // Which tabs the user is allowed to see (based on 200 vs 403 on fetch).
  const [access, setAccess] = useState({
    overview: true, policies: true, reports: true, wcag: true,
  });
  const [activeTab, setActiveTab] = useState('welcome');

  useEffect(() => {
    const token = localStorage.getItem('eyvo_client_token');
    if (!token) {
      navigate('/client-login');
      return;
    }
    
    fetchPortalData(token);
  }, [navigate]);

  const fetchPortalData = async (token) => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [complianceRes, policiesRes, reportsRes, wcagRes, scanRes] = await Promise.all([
        fetch(`${API_URL}/api/client-portal/compliance-status`, { headers }),
        fetch(`${API_URL}/api/client-portal/policies`, { headers }),
        fetch(`${API_URL}/api/client-portal/reports`, { headers }),
        fetch(`${API_URL}/api/client-portal/wcag-documents`, { headers }),
        fetch(`${API_URL}/api/client-portal/accessibility/scan-reports`, { headers }),
      ]);

      // Track which sections the user has access to. 403 = no scope → hide tab.
      // Both /wcag-documents and /accessibility/scan-reports live behind the
      // same client:wcag scope, so treat either 200 as "user has WCAG access".
      const nextAccess = {
        overview: complianceRes.ok,
        policies: policiesRes.ok,
        reports:  reportsRes.ok,
        wcag:     wcagRes.ok || scanRes.ok,
      };
      setAccess(nextAccess);

      if (complianceRes.ok) {
        const data = await complianceRes.json();
        setCompliance(data);
      }

      if (policiesRes.ok) {
        const data = await policiesRes.json();
        setPolicies(data.policies || []);
      }

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }

      if (wcagRes.ok) {
        const data = await wcagRes.json();
        setWcagDocs(data.documents || []);
      }

      if (scanRes.ok) {
        const data = await scanRes.json();
        setScanReportGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Portal fetch error:', error);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportId, fileName) => {
    const token = localStorage.getItem('eyvo_client_token');
    try {
      const response = await fetch(`${API_URL}/api/reports/${reportId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'report.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const downloadWcagDoc = async (doc) => {
    const token = localStorage.getItem('eyvo_client_token');
    try {
      const response = await fetch(`${API_URL}${doc.download_url}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`Downloaded ${doc.filename}`);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const downloadScanReport = async (report) => {
    const token = localStorage.getItem('eyvo_client_token');
    try {
      const response = await fetch(
        `${API_URL}/api/client-portal/accessibility/scan-reports/${report.report_id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.report_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Scan report downloaded');
    } catch (error) {
      toast.error('Failed to download scan report');
    }
  };

  if (loading) {
    return (
      <div className="client-portal loading">
        <Loader2 className="spin" size={40} />
        <p>Loading your compliance dashboard...</p>
      </div>
    );
  }

  return (
    <div className="client-portal">
      {/* Header */}
      <header className="portal-header">
        <div className="portal-logo">
          <ShieldCheck size={28} color="#2563eb" />
          <span><span className="accent">Eyvo</span> Client Portal</span>
        </div>
        <div className="portal-user">
          <span>{clientUser?.company_name || 'Client'}</span>
          <button onClick={clientLogout} className="logout-btn" data-testid="client-logout-btn">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Navigation Tabs — only shown for scopes the user has */}
      <nav className="portal-tabs">
        <button
          className={activeTab === 'welcome' ? 'active' : ''}
          onClick={() => setActiveTab('welcome')}
          data-testid="tab-welcome"
        >
          Welcome
        </button>
        {access.overview && (
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
            data-testid="tab-overview"
          >
            Overview
          </button>
        )}
        {access.policies && (
          <button
            className={activeTab === 'policies' ? 'active' : ''}
            onClick={() => setActiveTab('policies')}
            data-testid="tab-policies"
          >
            Policies
          </button>
        )}
        {access.reports && (
          <button
            className={activeTab === 'reports' ? 'active' : ''}
            onClick={() => setActiveTab('reports')}
            data-testid="tab-reports"
          >
            Reports
          </button>
        )}
        {access.wcag && (
          <button
            className={activeTab === 'wcag' ? 'active' : ''}
            onClick={() => setActiveTab('wcag')}
            data-testid="tab-wcag"
          >
            Accessibility
          </button>
        )}
      </nav>

      {/* Content */}
      <main className="portal-content">
        {/* Welcome tab — always visible, gives clients an immediate
            visual overview of what they can access + quick jump-ins. */}
        {activeTab === 'welcome' && (() => {
          const SCOPE_META = {
            overview: {
              tab: 'overview',
              label: 'Compliance Dashboard',
              icon: Shield,
              color: '#2563eb',
              description: "Real-time SOC 2 readiness posture, category-by-category scores, and control health.",
              count: compliance ? `${compliance.overall_compliance}% overall compliance` : 'Live posture',
            },
            policies: {
              tab: 'policies',
              label: 'SOC 2 Policies',
              icon: FileText,
              color: '#7c3aed',
              description: 'All information-security policies — read online or download as PDF.',
              count: `${policies.length} ${policies.length === 1 ? 'policy' : 'policies'} available`,
            },
            reports: {
              tab: 'reports',
              label: 'Audit Reports',
              icon: Download,
              color: '#059669',
              description: 'Monthly compliance summaries and pentest evidence, ready to download.',
              count: `${reports.length} ${reports.length === 1 ? 'report' : 'reports'} available`,
            },
            wcag: {
              tab: 'wcag',
              label: 'Accessibility',
              icon: Accessibility,
              color: '#0891b2',
              description: 'VPAT, WCAG 2.1 AA report, and monthly automated scans for the platform.',
              count: (() => {
                const scanTotal = scanReportGroups.reduce((n, g) => n + g.reports.length, 0);
                const parts = [];
                if (wcagDocs.length) parts.push(`${wcagDocs.length} ${wcagDocs.length === 1 ? 'document' : 'documents'}`);
                if (scanTotal)       parts.push(`${scanTotal} monthly ${scanTotal === 1 ? 'scan' : 'scans'}`);
                return parts.length ? parts.join(' · ') : 'VPAT & scans';
              })(),
            },
          };
          const grantedKeys = ['overview', 'policies', 'reports', 'wcag'].filter((k) => access[k]);
          const lockedKeys  = ['overview', 'policies', 'reports', 'wcag'].filter((k) => !access[k]);

          return (
            <div className="welcome-tab" data-testid="welcome-content">
              <div className="portal-welcome-hero" data-testid="portal-welcome-hero">
                <div className="portal-welcome-hero-inner">
                  <Sparkles size={32} className="portal-welcome-sparkle" />
                  <h1>Welcome{clientUser?.name ? `, ${clientUser.name.split(' ')[0]}` : ''}</h1>
                  <p>
                    You&apos;re signed in as <b>{clientUser?.email}</b> on behalf of <b>{clientUser?.company_name || 'your organisation'}</b>.
                    Your account has access to <b>{grantedKeys.length}</b> of 4 sections.
                    Pick one below or use the top nav to jump straight in.
                  </p>
                </div>
              </div>

              {grantedKeys.length > 0 && (
                <>
                  <h2 className="welcome-section-title" data-testid="welcome-granted-title">
                    <CheckCircle size={18} color="#10b981" /> Your access
                  </h2>
                  <div className="welcome-granted-grid" data-testid="welcome-granted-grid">
                    {grantedKeys.map((k) => {
                      const s = SCOPE_META[k];
                      const Icon = s.icon;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setActiveTab(s.tab)}
                          className="welcome-scope-card"
                          data-testid={`welcome-scope-${k}-granted`}
                          style={{ '--scope-color': s.color }}
                        >
                          <div className="welcome-scope-icon" style={{ background: `${s.color}22`, color: s.color }}>
                            <Icon size={26} />
                          </div>
                          <div className="welcome-scope-body">
                            <div className="welcome-scope-head">
                              <h3>{s.label}</h3>
                              <Badge color="#059669">Granted</Badge>
                            </div>
                            <p>{s.description}</p>
                            <div className="welcome-scope-meta">
                              <Clock size={13} /> {s.count}
                            </div>
                          </div>
                          <div className="welcome-scope-cta">
                            Open <ArrowRight size={16} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {grantedKeys.length === 0 && (
                <div className="welcome-empty" data-testid="welcome-empty">
                  <Lock size={28} color="#94a3b8" />
                  <h3>No sections granted yet</h3>
                  <p>
                    Your account is active, but no data sections have been assigned yet.
                    Reach out to your Eyvo point-of-contact to have scopes added.
                  </p>
                </div>
              )}

              {lockedKeys.length > 0 && (
                <>
                  <h2 className="welcome-section-title muted" data-testid="welcome-locked-title">
                    <Lock size={16} /> Available on request
                  </h2>
                  <div className="welcome-locked-grid" data-testid="welcome-locked-grid">
                    {lockedKeys.map((k) => {
                      const s = SCOPE_META[k];
                      const Icon = s.icon;
                      return (
                        <div
                          key={k}
                          className="welcome-locked-card"
                          data-testid={`welcome-scope-${k}-locked`}
                        >
                          <Icon size={18} color="#64748b" />
                          <div className="welcome-locked-text">
                            <span className="welcome-locked-title-txt">{s.label}</span>
                            <span className="welcome-locked-sub">{s.description}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              <div className="welcome-help" data-testid="welcome-help">
                <b>Need more access?</b>&nbsp;Contact your Eyvo point-of-contact and
                we&apos;ll grant any additional sections your team needs.
              </div>
            </div>
          );
        })()}

        {/* Overview Tab */}
        {activeTab === 'overview' && compliance && (
          <div className="overview-tab" data-testid="overview-content">
            <div className="welcome-banner">
              <h1>Welcome to Your Compliance Dashboard</h1>
              <p>View Eyvo&apos;s SOC 2 compliance status and download documentation.</p>
            </div>

            {/* Overall Score */}
            <div className="overall-score">
              <div className="score-ring">
                <ProgressRing 
                  pct={compliance.overall_compliance} 
                  size={160} 
                  stroke={12} 
                  color={compliance.overall_compliance >= 80 ? "#059669" : "#d97706"} 
                />
                <div className="score-value">
                  {compliance.overall_compliance}%
                </div>
              </div>
              <div className="score-info">
                <h2>{compliance.framework}</h2>
                <p>Overall Compliance Score</p>
                <Badge color={compliance.overall_compliance >= 80 ? "#059669" : "#d97706"}>
                  {compliance.overall_compliance >= 90 ? 'Excellent' : 
                   compliance.overall_compliance >= 70 ? 'Good' : 'In Progress'}
                </Badge>
              </div>
            </div>

            {/* Category Breakdown */}
            <h3 className="section-title">Trust Services Categories</h3>
            <div className="categories-grid">
              {compliance.categories?.map((cat) => {
                const meta = Object.values(CATEGORIES).find(c => c.label === cat.category) || 
                             { label: cat.category, icon: Shield, color: "#6b7280" };
                const Icon = meta.icon;
                return (
                  <Card key={cat.category} className="category-card">
                    <div className="card-header">
                      <Icon size={20} color={meta.color} />
                      <span>{cat.category}</span>
                    </div>
                    <div className="card-body">
                      <div className="pct" style={{ color: meta.color }}>
                        {cat.compliance_percentage}%
                      </div>
                      <Badge color={
                        cat.status === 'Operational' ? '#059669' :
                        cat.status === 'Monitoring' ? '#d97706' : '#6b7280'
                      }>
                        {cat.status}
                      </Badge>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Quick Links */}
            <div className="quick-links">
              <Card onClick={() => setActiveTab('policies')} className="link-card">
                <FileText size={24} color="#2563eb" />
                <div>
                  <h4>View Policies</h4>
                  <p>{policies.length} documents available</p>
                </div>
                <ChevronRight size={20} />
              </Card>
              <Card onClick={() => setActiveTab('reports')} className="link-card">
                <Download size={24} color="#059669" />
                <div>
                  <h4>Download Reports</h4>
                  <p>{reports.length} reports available</p>
                </div>
                <ChevronRight size={20} />
              </Card>
              <Card as="a" href="https://www.e-procurement.com/cloudinfrastructure" target="_blank" className="link-card">
                <ExternalLink size={24} color="#7c3aed" />
                <div>
                  <h4>Cloud Infrastructure</h4>
                  <p>Learn about our security</p>
                </div>
                <ChevronRight size={20} />
              </Card>
            </div>
          </div>
        )}

        {/* Policies Tab */}
        {activeTab === 'policies' && (
          <div className="policies-tab" data-testid="policies-content">
            <h2>Compliance Policies</h2>
            <p>Public policies available for your review.</p>

            <div className="policies-list">
              {policies.length === 0 ? (
                <Card className="empty-state">
                  <FileText size={40} color="#6b7280" />
                  <p>No public policies available at this time.</p>
                </Card>
              ) : (
                policies.map((policy) => (
                  <Card key={policy.policy_id} className="policy-card">
                    <div className="policy-info">
                      <h4>{policy.title}</h4>
                      <p>{policy.summary}</p>
                      <div className="policy-meta">
                        <Badge color="#6b7280">{policy.category}</Badge>
                        <span>Version {policy.version}</span>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="reports-tab" data-testid="reports-content">
            <h2>Compliance Reports</h2>
            <p>Download available compliance documentation.</p>

            <div className="reports-list">
              {reports.length === 0 ? (
                <Card className="empty-state">
                  <Download size={40} color="#6b7280" />
                  <p>No reports available at this time.</p>
                </Card>
              ) : (
                reports.map((report) => (
                  <Card key={report.report_id} className="report-card">
                    <div className="report-info">
                      <h4>{report.title}</h4>
                      <p>{report.description || `Generated ${new Date(report.generated_at).toLocaleDateString()}`}</p>
                      <Badge color="#2563eb">{report.report_type.replace(/_/g, ' ')}</Badge>
                    </div>
                    {report.file_available && (
                      <button
                        onClick={() => downloadReport(report.report_id, `${report.title}.pdf`)}
                        className="btn-download"
                        data-testid={`download-${report.report_id}`}
                      >
                        <Download size={18} />
                        Download
                      </button>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        )}
        {/* Accessibility (WCAG / VPAT) Tab */}
        {activeTab === 'wcag' && (
          <div className="wcag-tab" data-testid="wcag-content">
            <h2>Accessibility Documents</h2>
            <p>Current VPAT and WCAG 2.1 AA audit report for the Eyvo Procurement platform.</p>

            <div className="reports-list" data-testid="wcag-docs-client-list">
              {wcagDocs.length === 0 ? (
                <Card className="empty-state">
                  <FileText size={40} color="#6b7280" />
                  <p>No accessibility documents available at this time. Check back soon.</p>
                </Card>
              ) : (
                wcagDocs.map((doc) => (
                  <Card
                    key={doc.doc_id}
                    className="report-card"
                    data-testid={`wcag-client-${doc.doc_type}`}
                  >
                    <div className="report-info">
                      <h4>{doc.title}</h4>
                      <p>
                        {doc.filename} · Updated {new Date(doc.uploaded_at).toLocaleDateString()}
                      </p>
                      <Badge color="#0891b2">{doc.doc_type_label}</Badge>
                    </div>
                    <button
                      onClick={() => downloadWcagDoc(doc)}
                      className="btn-download"
                      data-testid={`wcag-client-download-${doc.doc_id}`}
                    >
                      <Download size={18} />
                      Download PDF
                    </button>
                  </Card>
                ))
              )}
            </div>

            {/* Monthly automated scan / audit reports (last 12 months) */}
            <h2 style={{ marginTop: 36 }}>Monthly Accessibility Audit Reports</h2>
            <p>
              Automated accessibility scans run against the Eyvo Procurement
              platform. Rolling 12-month window, grouped by month.
            </p>

            <div className="monthly-scans" data-testid="wcag-scan-reports-list">
              {scanReportGroups.length === 0 ? (
                <Card className="empty-state">
                  <FileText size={40} color="#6b7280" />
                  <p>No automated scan reports have been published in the last 12 months.</p>
                </Card>
              ) : (
                scanReportGroups.map((group) => (
                  <div
                    key={group.month_key}
                    style={{ marginBottom: 24 }}
                    data-testid={`wcag-scan-group-${group.month_key}`}
                  >
                    <h3 style={{ fontSize: 15, opacity: 0.85, margin: '12px 0 8px' }}>
                      {group.month_label}
                      <span style={{ opacity: 0.55, marginLeft: 8, fontWeight: 400 }}>
                        · {group.reports.length} scan{group.reports.length === 1 ? '' : 's'}
                      </span>
                    </h3>
                    <div className="reports-list">
                      {group.reports.map((r) => (
                        <Card
                          key={r.report_id}
                          className="report-card"
                          data-testid={`wcag-scan-${r.report_id}`}
                        >
                          <div className="report-info">
                            <h4>{r.label || r.scanner}</h4>
                            <p>
                              {r.scanner}
                              {r.target_url ? ` · ${r.target_url}` : ''}
                              {r.scan_date ? ` · ${r.scan_date}` : ''}
                            </p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                              <Badge color={r.overall_status === 'accessible' ? '#059669' : '#d97706'}>
                                {r.overall_status === 'accessible' ? 'Accessible' :
                                 r.overall_status === 'issues_found' ? 'Issues found' :
                                 r.overall_status.replace(/_/g, ' ')}
                              </Badge>
                              <Badge color="#6b7280">
                                {r.passed_elements}/{r.total_elements} passed · {r.score_pct}%
                              </Badge>
                            </div>
                          </div>
                          <button
                            onClick={() => downloadScanReport(r)}
                            className="btn-download"
                            data-testid={`wcag-scan-download-${r.report_id}`}
                          >
                            <Download size={18} />
                            Download PDF
                          </button>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="portal-footer">
        <p>© {new Date().getFullYear()} Eyvo Procurement. All rights reserved.</p>
        <Link to="/trust-center">View Public Trust Center</Link>
      </footer>
    </div>
  );
}

export default ClientPortal;
